import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const TOURNAMENT_LEVEL_LABELS = {
  championPrediction: 'Champion Prediction',
  playerOfTournament: 'Player of Tournament',
  semifinalistTeams: 'Semifinalist Teams',
  finalistTeams: 'Finalist Teams',
  top4Teams: 'Top 4 Teams',
  superOverTeam: 'Super Over Team',
};

const MATCH_LEVEL_LABELS = {
  matchWinner: 'Match Winner',
  manOfMatch: 'Man of the Match',
  tossWinner: 'Toss Winner',
  battingFirst: 'Batting First',
  highestRunScorer: 'Highest Run Scorer',
  highestWicketTaker: 'Highest Wicket Taker',
  highestRunConcedingBowler: 'Highest Run Conceding Bowler',
};

const PredictionItem = ({ label, points }) => (
  <div className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-lg">
    <div className="flex items-center gap-2">
      <Icon name="CheckCircle" size={16} color="var(--color-success, #22c55e)" />
      <span className="text-sm text-foreground">{label}</span>
    </div>
    <span className="text-sm font-semibold text-primary">{points} pts</span>
  </div>
);

const CollapsibleSection = ({ title, icon, iconColor, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/20 hover:bg-muted/40 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <Icon name={icon} size={18} color={iconColor} />
          <span className="text-base font-semibold text-foreground">{title}</span>
        </div>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={18} color="var(--color-muted-foreground)" />
      </button>
      {open && <div className="px-4 py-4">{children}</div>}
    </div>
  );
};

const OverviewTab = ({ tournament }) => {
  const predictionSettings = tournament?.predictionSettings;
  const tournamentLevel = predictionSettings?.tournamentLevel;
  const matchLevel = predictionSettings?.matchLevel;
  const bonusPoints = predictionSettings?.bonusPoints;

  const enabledTournamentPredictions = Object.entries(TOURNAMENT_LEVEL_LABELS)?.filter(
    ([key]) => tournamentLevel?.[key]?.enabled
  );

  const enabledMatchPredictions = Object.entries(MATCH_LEVEL_LABELS)?.filter(
    ([key]) => matchLevel?.[key]?.enabled
  );

  const earlyBonusEnabled = bonusPoints?.earlyPrediction?.enabled;
  const confidenceBonusEnabled = bonusPoints?.confidenceBonus?.enabled;

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Info" size={24} color="var(--color-primary)" />
          Tournament Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Format</p>
            <p className="text-base md:text-lg font-semibold text-foreground">{tournament?.format}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Teams</p>
            <p className="text-base md:text-lg font-semibold text-foreground">{tournament?.teams?.length} Teams</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Match Type</p>
            <p className="text-base md:text-lg font-semibold text-foreground">{tournament?.matchType}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Venue</p>
            <p className="text-base md:text-lg font-semibold text-foreground">{tournament?.location}</p>
          </div>
        </div>
        <div className="mt-6">
          <p className="text-sm text-muted-foreground mb-2">Description</p>
          <p className="text-sm md:text-base text-foreground leading-relaxed">{tournament?.description}</p>
        </div>
      </div>
      <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
          <Icon name="Users" size={24} color="var(--color-primary)" />
          Participating Teams
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tournament?.teams?.map((team) => (
            <div
              key={team?.id}
              className="bg-muted/50 rounded-lg p-4 flex items-center gap-3 hover:bg-muted transition-colors"
            >
              <Image
                src={team?.logo}
                alt={team?.logoAlt}
                className="w-12 h-12 object-contain"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground text-sm md:text-base truncate">{team?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{team?.country}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
          <Icon name="Award" size={24} color="var(--color-primary)" />
          Prediction Rules & Points System
        </h2>

        <div className="space-y-4">
          {/* Locking & Scoring Rules — first card */}
          <div className="p-4 bg-muted/20 rounded-lg border border-border">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
              <h3 className="text-base font-semibold text-foreground">Locking & Scoring Rules</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <Icon name="Clock" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">Tournament predictions lock <span className="font-semibold">1 hour before the first match</span></p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <Icon name="Clock" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">Match predictions lock <span className="font-semibold">1 hour before match start time</span></p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <Icon name="Calculator" size={16} color="var(--color-success, #22c55e)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">Points are <span className="font-semibold">calculated automatically</span> when results are posted</p>
              </div>
            </div>
          </div>

          {/* Tournament Level — collapsible, default collapsed */}
          <CollapsibleSection
            title="Tournament Level Points"
            icon="Trophy"
            iconColor="var(--color-primary)"
            defaultOpen={false}
          >
            {enabledTournamentPredictions?.length > 0 ? (
              <div className="space-y-2">
                {enabledTournamentPredictions?.map(([key, label]) => (
                  <PredictionItem
                    key={key}
                    label={label}
                    points={tournamentLevel?.[key]?.points}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No tournament-level predictions enabled.</p>
            )}
          </CollapsibleSection>

          {/* Match Level — collapsible, default collapsed */}
          <CollapsibleSection
            title="Match Level Points"
            icon="Swords"
            iconColor="var(--color-primary)"
            defaultOpen={false}
          >
            {enabledMatchPredictions?.length > 0 ? (
              <div className="space-y-2">
                {enabledMatchPredictions?.map(([key, label]) => (
                  <PredictionItem
                    key={key}
                    label={label}
                    points={matchLevel?.[key]?.points}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No match-level predictions enabled.</p>
            )}
          </CollapsibleSection>

          {/* Bonus Points */}
          {(earlyBonusEnabled || confidenceBonusEnabled) && (
            <div className="pt-2 border-t border-border space-y-4">
              <div className="flex items-center gap-2 mb-1 pt-2">
                <Icon name="Star" size={18} color="var(--color-accent)" />
                <h3 className="text-base font-semibold text-foreground">Bonus Points</h3>
              </div>

              {earlyBonusEnabled && (
                <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
                  <div className="flex items-start gap-3">
                    <Icon name="Zap" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-foreground text-sm md:text-base">Early Prediction Bonus</p>
                        <span className="text-sm font-semibold text-primary">+{bonusPoints?.earlyPrediction?.points} pts</span>
                      </div>
                      <p className="text-xs md:text-sm text-muted-foreground mt-1">
                        Submit predictions at least <span className="font-medium text-foreground">{bonusPoints?.earlyPrediction?.days} day(s)</span> before the deadline to earn this bonus.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {confidenceBonusEnabled && (
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-start gap-3">
                    <Icon name="TrendingUp" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-foreground text-sm md:text-base">Confidence Level Bonus</p>
                        <span className="text-sm font-semibold text-primary">up to +{bonusPoints?.confidenceBonus?.highConfidence} pts</span>
                      </div>
                      <p className="text-xs md:text-sm text-muted-foreground mt-1">
                        Use the confidence slider — earn <span className="font-medium text-foreground">+{bonusPoints?.confidenceBonus?.highConfidence} pts</span> for high confidence and <span className="font-medium text-foreground">+{bonusPoints?.confidenceBonus?.mediumConfidence} pts</span> for medium confidence predictions.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;